import { useEffect, useState } from "react";
import MDEditor from "@uiw/react-md-editor";
import { ExternalLink, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../api/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { toast } from "sonner";

const STATUS_LABELS = { draft: "Borrador", review: "En revisión", approved: "Aprobado", archived: "Archivado", obsolete: "Obsoleto" };
const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700",
  review: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  archived: "bg-indigo-100 text-indigo-700 border-indigo-200",
  obsolete: "bg-slate-100 text-slate-400 line-through",
};

export default function DocViewModal({ docId, onClose }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/documents/${docId}`);
        setDoc(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [docId, onClose]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="doc-view-modal" className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <span className="truncate">{doc?.title || "Documento"}</span>
            {doc && <Badge className={STATUS_CLS[doc.status]}>{STATUS_LABELS[doc.status]}</Badge>}
            {doc && <span className="text-[12px] text-slate-400">v{doc.current_version}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 -mx-1 px-1" data-color-mode="light">
          {loading && (
            <div className="py-12 text-center text-slate-500" data-testid="doc-view-loading">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Cargando documento...
            </div>
          )}
          {!loading && doc && (
            doc.content?.trim()
              ? <MDEditor.Markdown source={doc.content} className="!bg-white text-slate-800 text-[14px]" data-testid="doc-view-content" />
              : <p className="text-slate-400 text-[14px] py-8 text-center">Este documento aún no tiene contenido.</p>
          )}
        </div>
        {doc && (
          <div className="flex justify-end pt-2 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={() => navigate(`/documents/${doc.id}`)} data-testid="doc-view-open-editor">
              <ExternalLink className="h-4 w-4 mr-1" /> Abrir en el editor
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
